import { useState, useEffect, useCallback } from 'react';
import { SyncStatus } from '../types/orders';
import { fetchSyncStatus as fetchSyncStatusApi, syncOrders, syncProducts } from '../services/b2bSyncApi';

interface UseSyncStatusReturn {
  syncStatus: {
    orders?: SyncStatus;
    products?: SyncStatus;
  } | null;
  loading: boolean;
  error: string;
  syncingOrders: boolean;
  syncingProducts: boolean;
  refetch: () => Promise<void>;
  syncOrders: () => Promise<{ success: boolean; synced: number; eligible: number }>;
  syncProducts: () => Promise<{ success: boolean; synced: number; unmapped: number }>;
}

export const useSyncStatus = (): UseSyncStatusReturn => {
  const [syncStatus, setSyncStatus] = useState<{ orders?: SyncStatus; products?: SyncStatus } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [syncingProducts, setSyncingProducts] = useState(false);

  const loadSyncStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchSyncStatusApi();
      if (data.success) {
        setSyncStatus(data.syncStatus);
      }
    } catch (err: any) {
      console.error('Error fetching sync status:', err);
      setError(err.response?.data?.message || 'Failed to fetch sync status');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncOrders = useCallback(async () => {
    try {
      setSyncingOrders(true);
      setError('');
      const result = await syncOrders();
      await loadSyncStatus();
      return result;
    } catch (err: any) {
      console.error('Error syncing orders:', err);
      setError(err.response?.data?.message || 'Failed to sync orders');
      throw err;
    } finally {
      setSyncingOrders(false);
    }
  }, [loadSyncStatus]);

  const handleSyncProducts = useCallback(async () => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    try {
      setSyncingProducts(true);
      setError('');
      
      // Start polling for progress
      pollInterval = setInterval(async () => {
        try {
          const data = await fetchSyncStatusApi();
          if (data.success && data.syncStatus.products) {
            setSyncStatus(prev => ({
              ...prev,
              products: data.syncStatus.products
            }));
            
            // Stop polling if sync is complete
            if (data.syncStatus.products.status !== 'in_progress') {
              if (pollInterval) clearInterval(pollInterval);
            }
          }
        } catch (err) {
          console.error('Error polling sync status:', err);
        }
      }, 500); // Poll every 500ms
      
      const result = await syncProducts();
      if (pollInterval) clearInterval(pollInterval);
      await loadSyncStatus();
      return result;
    } catch (err: any) {
      console.error('Error syncing products:', err);
      if (pollInterval) clearInterval(pollInterval);
      setError(err.response?.data?.message || 'Failed to sync products');
      throw err;
    } finally {
      setSyncingProducts(false);
    }
  }, [loadSyncStatus]);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  return {
    syncStatus,
    loading,
    error,
    syncingOrders,
    syncingProducts,
    refetch: loadSyncStatus,
    syncOrders: handleSyncOrders,
    syncProducts: handleSyncProducts
  };
};

